import CloseIcon from "@mui/icons-material/Close";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import {
    Box,
    Button,
    MenuItem,
    MobileStepper,
    TextField,
    Typography
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { streetVendorTypes } from "../../utils/streetVendorTypes";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import LockIcon from "@mui/icons-material/Lock";
import Grid from '@mui/material/Grid2'; // Using GridV2
import { useTheme } from "@mui/material/styles";
import React, { useState, useEffect, useCallback } from "react";
import { createStoreFrontServiceAuth, fetchMyStorefront, fetchVendorDataService, updateStoreFrontServiceAuth, uploadMenuPhotoServiceAuth, uploadStorePhotoServiceAuth } from "../../services/appServices";
import { useNavigate, useParams } from "react-router-dom";
import { theme } from "../../theme";
import { createInputSX } from "../../utils/constants";
import CreateEditFormComponent from "../components/CreateEditFormComponent";
import { generateSocialMediaUrl, parseAllSocialMediaUrls } from "../../utils/helperUtils";
import LoadingComponent from "../components/LoadingComponent";
import { useAuth } from "../../context/AuthContext";
import { useSnackbar } from "../../context/SnackbarContext";

const steps = [
    "🛍️ Storefront Basics",
    "📞 Contact Info",
    "📱 Socials (Optional)",
    "📸 Upload Photos",
    "📝 Final Review",
];

const socialMediaOptions = [
    { label: "Instagram", value: "instagram" },
    { label: "X (Twitter)", value: "x" },
    { label: "TikTok", value: "tiktok" },
    { label: "Facebook", value: "facebook" },
];

function CreateEditStorefrontView() {
    const { slug } = useParams();

    if (slug) {
        const uploadPhotosIndex = steps.indexOf("📸 Upload Photos");
        if (uploadPhotosIndex !== -1) {
            steps.splice(uploadPhotosIndex, 1);
        }
    }

    const maxSteps = steps.length ;
    const { login, logout } = useAuth();
    const showSnackbar = useSnackbar();
    const navigate = useNavigate();

    const [activeStep, setActiveStep] = useState(0);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [usernameStatus, setUsernameStatus] = useState({ message: "", error: false });

    const [formData, setFormData] = useState({
        logo: null,
        name: "",
        username: "",
        description: "",
        phone: "",
        zip: "",
        cuisine: "",
        socialMedia: [],
        storefrontPhotos: [],
        menuPhotos: [],
    });

    // Phone error state
    const [phoneError, setPhoneError] = useState("");

    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxImages, setLightboxImages] = useState([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);

        useEffect(() => {
            const fetchStorefrontData = async () => {
                if (!slug) return;
    
                setLoading(true);
                try {
                    const response = await fetchMyStorefront(login, logout);
                    if (response.success && response.data) {
                        const data = response.data;
    
                        setFormData(prev => ({
                            ...prev,
                            logo: data.logo || null,
                            name: data.name || "",
                            phone: data.phone_number || "",
                            username: data.slug || "",
                            cuisine: data.cuisine || "",
                            description: data.description || "",
                            zip: data.zipcode || "",
                            socialMedia: parseAllSocialMediaUrls({
                                website_url: data.website_url,
                                instagram_url: data.instagram_url,
                                x_url: data.x_url,
                                tiktok_url: data.tiktok_url,
                                facebook_url: data.facebook_url,
                            })
                        }));
                    }
                } catch (err) {
                    console.error("Failed to load storefront data:", err);
                    setError(err.message || "Something went wrong.");
                } finally {
                    setLoading(false);
                }
            };
    
            fetchStorefrontData();
        }, [slug]);

    useEffect(() => {
        if (!formData.username || formData.username == slug) {
            setUsernameStatus({ message: "", error: false });
            return;
        }

        const normalizedUsername = formData.username.toLowerCase();
        const isValid = /^[a-z0-9._-]+$/.test(normalizedUsername);

        if (!isValid) {
            setUsernameStatus({ message: "Username contains invalid characters", error: true });
            return;
        }

        const debounceTimeout = setTimeout(async () => {
            try {
                const response = await fetchVendorDataService(normalizedUsername);
                if (response.success) {
                    setUsernameStatus({ message: "Username already taken", error: true });
                } else {
                    setUsernameStatus({ message: "Username is available", error: false });
                }
            } catch {
                setUsernameStatus({ message: "", error: false });
            }
        }, 1000);

        return () => clearTimeout(debounceTimeout);
    }, [formData.username]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: name === "username" ? value.toLowerCase() : value,
        });
    };

    const handlePhotoUpload = (type, event) => {
        const files = Array.from(event.target.files);
        setFormData({
            ...formData,
            [type]: [...formData[type], ...files],
        });
    };

    const handleRemovePhoto = (type, index) => {
        const updated = formData[type].filter((_, i) => i !== index);
        setFormData({ ...formData, [type]: updated });
    };

    const handlePreviewPhoto = (photo, index, type) => {
        const photoArray = type == "storefront"
            ? formData.storefrontPhotos
            : formData.menuPhotos;

        const slides = photoArray.map((p) => ({
            src: typeof p === "string" ? p : URL.createObjectURL(p),
        }));

        setLightboxImages(slides);
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    const handleChangeSocialMedia = (index, field, value) => {
        const updatedLinks = [...formData.socialMedia];
        updatedLinks[index][field] = value;
        setFormData({ ...formData, socialMedia: updatedLinks });
    };

    const handleAddSocialMedia = () => {
        if (formData.socialMedia.length < 2) {
            setFormData({
                ...formData,
                socialMedia: [...formData.socialMedia, { platform: "", username: "" }],
            });
        }
    };

    const handleRemoveSocialMedia = (index) => {
        const updated = formData.socialMedia.filter((_, i) => i !== index);
        setFormData({ ...formData, socialMedia: updated });
    };

    // Phone number formatting for display
    const formatPhoneNumber = (value) => {
        const cleaned = ("" + value).replace(/\D/g, "");
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{0,4})$/);
        if (match) {
            return `(${match[1]}) ${match[2]}${match[3] ? "-" + match[3] : ""}`;
        }
        return value;
    };

    const formattedPhone = formatPhoneNumber(formData.phone);

    const validateStep = () => {
        switch (activeStep) {
            case 0:
                return (
                    formData.name.trim() !== "" &&
                    formData.username.trim() !== "" &&
                    usernameStatus.error !== true
                    // && formData.description.trim() !== ""
                );
            case 1:
                return formData.phone.trim() !== "" &&
                    formData.zip.trim() !== "" &&
                    formData.cuisine.trim() !== "";
            case 2:
                return formData.socialMedia.every(
                    (s) => s.platform.trim() !== "" && s.username.trim() !== ""
                );
            default:
                return true;
        }
        // return true
    };

    const handleNext = () => {
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const handleSubmit = async () => {
        const data = {
            ...(formData?.logo && { logo: formData?.logo }),
            name: formData.name,
            phone_number: formData.phone,
            slug: formData.username,
            cuisine: formData.cuisine,
            description: formData.description,
            ...(formData.socialMedia.length > 0 && generateSocialMediaUrl(formData.socialMedia)),
            zipcode: formData.zip
        };

        console.log("Form Data Submitted:", data);
        setLoading(true);

        try {
            let response;
            try {
                response = await createStoreFrontServiceAuth(data, login, logout);
            } catch (err) {
                throw new Error(err.message);
            }

            if (formData.menuPhotos.length > 0) {
                try {
                    await uploadMenuPhotoServiceAuth(formData.menuPhotos, formData.username, login, logout);
                } catch (err) {
                    // throw new Error(err.message);
                    console.error("uploadMenuPhotoServiceAuth error:", err);
                    alert("There was an error uploading your photos, Don't worry! You can add them later")
                }
            }

            if (formData.storefrontPhotos.length > 0) {
                try {
                    await uploadStorePhotoServiceAuth(formData.storefrontPhotos, formData.username, login, logout);
                } catch (err) {
                    // throw new Error(err.message);
                    console.error("uploadStorePhotoServiceAuth error:", err);
                    alert("There was an error uploading your photos, Don't worry! You can add them later")
                }
            }

            if (response.success) {
                showSnackbar("✅ Storefront created successfully!", "success");
                navigate("/_/storefront/manage");
            }
            else {
                setError(`${response.error}` || "Something went wrong.");
            }
        } catch (error) {
            console.error("Submittion error:", error);
            setError(`${error.message}` || "Something went wrong.");
        }
        finally {
            setTimeout(() => {
                setLoading(false);
            }, 3000);
        }
    };

    const handleUpdate = async () => {
        if (!slug) {
            setError("No storefront slug provided for update.");
            return;
        }

        const data = {
            ...(formData?.logo && typeof formData?.logo !== "string" && { logo: formData?.logo }),
            name: formData.name,
            phone_number: formData.phone,
            slug: formData.username,
            cuisine: formData.cuisine,
            description: formData.description,
            ...(formData.socialMedia.length > 0 && generateSocialMediaUrl(formData.socialMedia)),
            zipcode: formData.zip
        };

        setLoading(true);
        try {
            const response = await updateStoreFrontServiceAuth(slug, data, login, logout);
            if (response.success) {
                showSnackbar("✅ Storefront updated successfully!", "success");
                navigate("/_/storefront/manage");
            }
            else {
                setError(`${response.error}` || "Something went wrong.");
            }
        } catch (error) {
            console.error("Update error:", error);
            setError(`${error.message}` || "Something went wrong.");
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 3000);
        }
    };


    if (loading) {
        return (
            <LoadingComponent/>
        );
    }

    return (
        <>
            <CreateEditFormComponent
                formBgColor={theme.colors.primaryLight}
                bgColor={theme.colors.events.secondary}
                title={slug ? "Update Storefront ✏️" : "Create a Storefront 🚀"}
                titleColor={theme.colors.primary}
                maxSteps={maxSteps}
                validateStep={validateStep}
                activeStep={activeStep}
                slug={slug}
                error={error}
                handleBack={handleBack}
                handleNext={handleNext}
                handleSubmit={handleSubmit}
                handleUpdate={handleUpdate}
                setError={setError}
                backStepperColor={theme.colors.events.secondary}
            >
                {/* Stepper Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: 'center',
                        pt: 2,
                        pb: 1
                    }}
                >
                    <Typography variant="h6" sx={{ color: theme.colors.events.secondary, fontWeight: 'bold' }} gutterBottom>{steps[activeStep]}</Typography>
                </Box>

                {/* Step Content */}
                <Box sx={{ paddingX: 3, pb: 2 }} >
                    {activeStep === 0 && (
                        <Grid container spacing={2} >
                            <Grid size={{ xs: 12 }}>
                                <Box display="flex" alignItems="center" gap={1} justifyContent={'center'} flexDirection="column">
                                    {formData?.logo ? (
                                        <Avatar
                                                src={typeof formData?.logo == "object" ? URL.createObjectURL(formData?.logo) : formData?.logo}
                                            sx={{ width: 70, height: 70 }}
                                        />
                                    ) : (
                                        <IconButton
                                            component="label"
                                            sx={{
                                                width: 70,
                                                height: 70,
                                                borderRadius: "50%",
                                                border: `2px dashed ${theme.colors.primary}`,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <input
                                                hidden
                                                accept="image/*"
                                                type="file"
                                                onChange={(e) => {
                                                    if (e.target.files.length) {
                                                        setFormData({ ...formData, logo: e.target.files[0] });
                                                    }
                                                }}
                                            />
                                            <AddAPhotoIcon />
                                        </IconButton>
                                    )}
                                    {formData.logo ? (
                                        <Button
                                            size="small"
                                            onClick={() => setFormData({ ...formData, logo: null })}
                                            sx={{ color: theme.colors.events.secondary }}
                                        >
                                            <CloseIcon fontSize="small" />Remove logo
                                        </Button>
                                    ) : (
                                        <Typography variant="caption" color={theme.colors.events.secondary} fontSize={'small'}>
                                            Upload storefront logo
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Storefront Name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    variant="standard"
                                    sx={createInputSX}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Storefront Username"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleChange}
                                    variant="standard"
                                    sx={createInputSX}
                                    error={usernameStatus.error}
                                    helperText={usernameStatus.message}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    label="Description (Optional)"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    variant="standard"
                                    sx={createInputSX}
                                    inputProps={{ maxLength: 80 }}
                                    helperText={`${formData.description.length}/80`}
                                />
                            </Grid>
                        </Grid>
                    )}
                    {activeStep === 1 && (
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Phone Number"
                                    name="phone"
                                    value={formatPhoneNumber(formData.phone)} // keep formatting for display
                                    onChange={(e) => {
                                        // Remove all non-digit characters
                                        const numericValue = e.target.value.replace(/\D/g, "");

                                        if (numericValue.length <= 10) {
                                            setFormData({ ...formData, phone: numericValue }); // store only digits
                                            setPhoneError("");
                                        } else {
                                            setPhoneError("Phone number cannot exceed 10 digits");
                                        }
                                    }}
                                    variant="standard"
                                    sx={createInputSX}
                                    error={Boolean(phoneError)}
                                    helperText={phoneError}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Tooltip
                                                    title="This isn't shared with anyone"
                                                    enterTouchDelay={0}
                                                    leaveTouchDelay={3000}
                                                    placement="bottom-start"
                                                    arrow
                                                >
                                                    <IconButton>
                                                        <LockIcon sx={{ color: theme.colors.events.primaryDark }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Zip Code"
                                    name="zip"
                                    value={formData.zip}
                                    onChange={handleChange}
                                    variant="standard"
                                    type="number"
                                    sx={createInputSX}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Autocomplete
                                    fullWidth
                                    options={streetVendorTypes}
                                    value={formData.cuisine}
                                    onChange={(_, newValue) => setFormData({ ...formData, cuisine: newValue || "" })}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="What do you sell *"
                                            variant="standard"
                                            sx={createInputSX}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    )}
                    {activeStep === 2 && (

                        <Grid container spacing={2}>
                            {formData.socialMedia.map((social, index) => (
                                <React.Fragment key={index}>
                                    <Grid size={{ xs: 6 }}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Platform"
                                            value={social.platform}
                                            onChange={(e) =>
                                                handleChangeSocialMedia(index, "platform", e.target.value)
                                            }
                                            variant="standard"
                                            sx={createInputSX}
                                        >
                                            {socialMediaOptions.filter((option) => {
                                                const selectedPlatforms = formData.socialMedia.map((s) => s.platform);
                                                return !selectedPlatforms.includes(option.value) || option.value === social.platform;
                                            }).map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <TextField
                                            fullWidth
                                            label="Username"
                                            value={social.username}
                                            onChange={(e) =>
                                                handleChangeSocialMedia(index, "username", e.target.value)
                                            }
                                            variant="standard"
                                            sx={createInputSX}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <Button
                                            sx={{ color: theme.colors.events.primaryDark }}
                                            onClick={() => handleRemoveSocialMedia(index)}
                                            startIcon={<CloseIcon />}
                                        >
                                            Remove
                                        </Button>
                                    </Grid>
                                </React.Fragment>
                            ))}
                            {formData.socialMedia.length < 2 && (
                                <Grid size={{ xs: 12 }}>
                                    <Button sx={{ color: theme.colors.events.secondary }} onClick={handleAddSocialMedia}>+ Add Social Media</Button>
                                </Grid>
                            )}
                        </Grid>
                    )}
                    {!slug && activeStep === 3 && (

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="subtitle1" color={theme.colors.events.secondary}>Storefront Photos (Optional)</Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    component="label"
                                    sx={{ mt: 1, bgcolor: theme.colors.primary, borderRadius: 4, color: theme.colors.events.secondary }}
                                >
                                    + Upload Photos
                                    <input
                                        hidden
                                        multiple
                                        accept="image/*"
                                        type="file"
                                        onChange={(e) => handlePhotoUpload("storefrontPhotos", e)}
                                    />
                                </Button>
                                <Grid container spacing={1} sx={{ mt: 1 }}>
                                    {formData.storefrontPhotos.map((photo, idx) => (
                                        <Grid key={idx} xs={3} sm={2}>
                                            <Box sx={{ position: "relative", width: 80, height: 64, borderRadius: 2, overflow: "hidden", border: `0.5px solid ${theme.colors.primary}` }}>
                                                <img
                                                    src={typeof photo === "string" ? photo : URL.createObjectURL(photo)}
                                                    alt={`storefront-${idx}`}
                                                    onClick={() => handlePreviewPhoto(photo, idx, "storefront")}
                                                    style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }}
                                                />
                                                <IconButton
                                                    size="small"
                                                    sx={{
                                                        position: "absolute",
                                                        top: 2,
                                                        right: 2,
                                                        bgcolor: "rgba(255,255,255,0.7)",
                                                        zIndex: 2
                                                    }}
                                                    onClick={() => handleRemovePhoto("storefrontPhotos", idx)}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="subtitle1" color={theme.colors.events.secondary}>Menu/Item Photos (Optional)</Typography>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    size="small"
                                    sx={{ mt: 1, bgcolor: theme.colors.primary, borderRadius: 4, color: theme.colors.events.secondary }}
                                >
                                    + Upload Photos
                                    <input
                                        hidden
                                        multiple
                                        accept="image/*"
                                        type="file"
                                        onChange={(e) => handlePhotoUpload("menuPhotos", e)}
                                    />
                                </Button>
                                <Grid container spacing={1} sx={{ mt: 1 }}>
                                    {formData.menuPhotos.map((photo, idx) => (
                                        <Grid key={idx} xs={3} sm={2}>
                                            <Box sx={{ position: "relative", width: 80, height: 64, borderRadius: 2, overflow: "hidden", border: `1px solid ${theme.colors.primary}` }}>
                                                <img
                                                    src={typeof photo === "string" ? photo : URL.createObjectURL(photo)}
                                                    alt={`menu-${idx}`}
                                                    onClick={() => handlePreviewPhoto(photo, idx, "menu")}
                                                    style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }}
                                                />
                                                <IconButton
                                                    size="small"
                                                    sx={{
                                                        position: "absolute",
                                                        top: 2,
                                                        right: 2,
                                                        bgcolor: "rgba(255,255,255,0.7)",
                                                        zIndex: 2
                                                    }}
                                                    onClick={() => handleRemovePhoto("menuPhotos", idx)}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                    { ((slug && activeStep == 3) || activeStep === 4) && (
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <Card sx={{ p: 1, borderRadius: 4 }}>
                                    <CardContent>
                                        <Typography pb={1}><b>🏪 Name:</b> {formData.name}</Typography>
                                        <Typography pb={1}><b>🔗 Username:</b> {formData.username}</Typography>
                                        <Typography><b>📝 Description:</b> {formData.description || "N/A"}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <Card sx={{ p: 1, borderRadius: 4 }}>
                                    <CardContent>
                                        <Typography pb={1}><b>📞 Phone:</b> {formData.phone}</Typography>
                                        <Typography pb={1}><b>📍 Zip:</b> {formData.zip}</Typography>
                                        <Typography><b>🍽️ Cuisine:</b> {formData.cuisine}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <Card sx={{ p: 1, borderRadius: 4 }}>
                                    <CardContent>
                                        <Typography><b>📱 Social Media</b></Typography>
                                        {formData.socialMedia.length > 0 ? (
                                            formData.socialMedia.map((s, i) => (
                                                <Typography key={i} pt={1}> <b>{s.platform}</b>: {s.username}</Typography>
                                            ))
                                        ) : (
                                            <Typography>None</Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            {!slug &&
                            <Grid size={{ xs: 12 }}>
                                <Card sx={{ p: 1, borderRadius: 4 }}>
                                    <CardContent>
                                        <Typography pb={1}><b>🖼️ Storefront Photos:</b> {formData.storefrontPhotos.length} added</Typography>
                                        <Typography><b>🍔 Menu/Item Photos:</b> {formData.menuPhotos.length} added</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>}
                        </Grid>
                    )}
                </Box>
            </CreateEditFormComponent>
            <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={lightboxImages}
                index={lightboxIndex}
                carousel={{ finite: lightboxImages.length <= 1, preload: 0 }}
                render={{
                    buttonClose: () => (
                        <IconButton
                            onClick={() => setLightboxOpen(false)}
                            sx={{
                                top: 80,
                                right: 16,
                                zIndex: 9999,
                                backgroundColor: 'rgba(255,255,255,0.7)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                }
                            }}
                        >
                            <CloseIcon sx={{ color: theme.colors.events.primaryDark }} />
                        </IconButton>
                    )
                }}
            />
        </>
    );
}

export default CreateEditStorefrontView;